/**
 * "Smart album" auto-fill. Optional button, never the default path (docs/00-decisions.md).
 *
 * Chronological strategy:
 *   1. sort the tray by EXIF takenAt, photos without a date last (stable order);
 *   2. start a new group whenever the gap to the previous photo is bigger than `gapMs` (2 h);
 *   3. split groups bigger than the largest template into even chunks;
 *   4. pick a template per group from the group size, the group's dominant photo orientation and
 *      the album's format family;
 *   5. hand each photo to the slot whose shape suits it (`orderForSlots`), so an upright picture
 *      lands in an upright hole and a wide one in a wide hole;
 *   6. emit `spread.applyLayout` commands, growing the album first if it is too short.
 *
 * The function is pure and deterministic: same input, same commands. Nothing is applied here —
 * the caller pipes the commands through `History.apply` so the whole fill is one undo step
 * (pass a `coalesceKey`, or wrap them in a single `{ type: 'batch' }`).
 */
import {
  PAGE_COUNT_LIMITS,
  familyOf,
  neutralImageFit,
  spreadCountForPages,
  sortElements,
  type AlbumDocument,
  type Command,
  type ElementTarget,
  type Layout,
  type LayoutFamily,
  type PhotoInfo,
} from '@freealbum/document';
import { MAX_LAYOUT_PHOTOS, getLayouts } from './layouts/index';

export type AutoFillStrategy = 'chronological';

export interface AutoFillOptions {
  photos: readonly PhotoInfo[];
  doc: AlbumDocument;
  /** first spread to fill; defaults to the first full spread (skipping a lone opening page) */
  fromSpread?: number;
  /** Fill this surface's existing photo slots first; cover never falls through to inner pages. */
  target?: ElementTarget;
  strategy?: AutoFillStrategy;
  /** gap that starts a new group, in ms (default 2 hours) */
  gapMs?: number;
  /** never put more than this many photos on one spread (default: the biggest template) */
  maxPerSpread?: number;
  /** grow the album with `album.setPageCount` when there are not enough spreads (default true) */
  growAlbum?: boolean;
}

export const DEFAULT_GAP_MS = 2 * 60 * 60 * 1000;

export type Orientation = 'landscape' | 'portrait' | 'square';

function orientationOf(p: PhotoInfo): Orientation {
  const r = p.width / p.height;
  if (r > 1.15) return 'landscape';
  if (r < 0.87) return 'portrait';
  return 'square';
}

/**
 * Reorder a group so each photo lands in the slot that suits its shape.
 *
 * `materializeLayout` fills slots in order from the list it is given, so this is where a portrait
 * picture stops being stretched into a letterbox: sort both sides by aspect and pair them off. For
 * a one-dimensional match that pairing is optimal — any swap between two sorted pairs makes the
 * total mismatch worse — and it needs no search, which keeps the whole fill pure and instant.
 *
 * Mismatch is measured on log aspect, so 2:1 is as far from 1:1 as 1:2 is, rather than the raw
 * ratio's lopsided distances. Ties keep the original order, so the result is deterministic.
 *
 * This deliberately breaks strict chronology *inside* a spread. Between spreads the story still
 * runs in order; within one, shape wins, because a row of four where two are letterboxed reads
 * worse than four that fit.
 */
export function orderForSlots(
  photos: readonly PhotoInfo[],
  slots: readonly { w: number; h: number }[],
  boxAspect: number,
): PhotoInfo[] {
  if (photos.length === 0 || slots.length === 0) return [...photos];
  const byAspect = <T,>(items: readonly T[], aspect: (item: T) => number) =>
    items.map((item, index) => ({ item, index, a: Math.log(aspect(item)) }))
      .sort((x, y) => x.a - y.a || x.index - y.index);

  const sortedPhotos = byAspect(photos, (p) => (p.height > 0 ? p.width / p.height : 1));
  // A slot's shape is its fractions scaled by the box it sits in, not the fractions alone.
  const sortedSlots = byAspect(slots.slice(0, photos.length), (s) => (s.h > 0 ? (s.w * boxAspect) / s.h : 1));

  const out: PhotoInfo[] = [];
  sortedSlots.forEach((slot, rank) => {
    const photo = sortedPhotos[rank];
    if (photo) out[slot.index] = photo.item;
  });
  // Any photo beyond the slot count keeps its place at the end, in its original order.
  const placed = new Set(out);
  return [...out.filter(Boolean), ...photos.filter((p) => !placed.has(p))];
}

/** Usable photos, chronological, undated last, original order preserved within each bucket. */
export function sortPhotosChronologically(photos: readonly PhotoInfo[]): PhotoInfo[] {
  return photos
    .filter((p) => p.status !== 'failed' && p.status !== 'purged')
    .map((p, i) => ({ p, i, t: p.takenAt ? Date.parse(p.takenAt) : Number.NaN }))
    .sort((a, b) => {
      const an = Number.isNaN(a.t);
      const bn = Number.isNaN(b.t);
      if (an !== bn) return an ? 1 : -1;
      if (!an && a.t !== b.t) return a.t - b.t;
      return a.i - b.i;
    })
    .map((x) => x.p);
}

/** Split `n` items into chunks of at most `max`, as evenly as possible (9 → 5+4, not 8+1). */
export function chunkEvenly(n: number, max: number): number[] {
  if (n <= 0) return [];
  if (n <= max) return [n];
  const parts = Math.ceil(n / max);
  const base = Math.floor(n / parts);
  const extra = n % parts;
  return Array.from({ length: parts }, (_, i) => base + (i < extra ? 1 : 0));
}

/** Group by time gap, then cap each group at `maxPerSpread`. */
export function groupPhotos(
  photos: readonly PhotoInfo[],
  gapMs = DEFAULT_GAP_MS,
  maxPerSpread = MAX_LAYOUT_PHOTOS,
): PhotoInfo[][] {
  const sorted = sortPhotosChronologically(photos);
  const runs: PhotoInfo[][] = [];
  let prev = Number.NaN;
  for (const p of sorted) {
    const t = p.takenAt ? Date.parse(p.takenAt) : Number.NaN;
    const sameRun =
      runs.length > 0 && (Number.isNaN(t) || Number.isNaN(prev) ? Number.isNaN(t) && Number.isNaN(prev) : t - prev <= gapMs);
    if (sameRun) runs[runs.length - 1]!.push(p);
    else runs.push([p]);
    prev = t;
  }
  const out: PhotoInfo[][] = [];
  for (const run of runs) {
    let i = 0;
    for (const size of chunkEvenly(run.length, Math.max(1, maxPerSpread))) {
      out.push(run.slice(i, i + size));
      i += size;
    }
  }
  return out;
}

const ORIENTATION_TAGS: Record<Orientation, string[]> = {
  landscape: ['row', 'wide', 'stack', 'for-landscape', 'filmstrip', 'panorama'],
  portrait: ['grid', 'column', 'for-portrait', 'pairs', 'mosaic'],
  square: ['grid', 'even', 'mosaic'],
};

export function dominantOrientation(group: readonly PhotoInfo[]): Orientation {
  const counts: Record<Orientation, number> = { landscape: 0, portrait: 0, square: 0 };
  for (const p of group) counts[orientationOf(p)]++;
  if (counts.landscape >= counts.portrait && counts.landscape >= counts.square) return 'landscape';
  if (counts.portrait >= counts.square) return 'portrait';
  return 'square';
}

function scoreLayout(l: Layout, orientation: Orientation, family: LayoutFamily): number {
  let score = 0;
  if (l.families.includes(family)) score += 4;
  for (const t of ORIENTATION_TAGS[orientation]) if (l.tags.includes(t)) score += 2;
  // auto-fill cannot write copy, so prefer templates with no empty text slots
  if (l.textCount === 0) score += 3;
  // one full-bleed photo reads as a deliberate statement; keep it rare
  if (l.tags.includes('full-bleed')) score -= 1;
  return score;
}

export interface PickLayoutOptions {
  photoCount: number;
  orientation: Orientation;
  family: LayoutFamily;
  scope: 'spread' | 'page';
  /** rotates between equally good templates so consecutive spreads differ */
  variant?: number;
}

/** Best template for a group, or undefined when nothing matches. Deterministic. */
export function pickLayout(opts: PickLayoutOptions): Layout | undefined {
  const exact = getLayouts({ photoCount: opts.photoCount, scope: opts.scope });
  const pool = exact.length > 0 ? exact : getLayouts({ photoCount: opts.photoCount });
  if (pool.length === 0) return undefined;
  const scored = pool
    .map((l) => ({ l, s: scoreLayout(l, opts.orientation, opts.family) }))
    .sort((a, b) => b.s - a.s || a.l.id.localeCompare(b.l.id));
  const best = scored[0]!.s;
  const top = scored.filter((x) => x.s === best).map((x) => x.l);
  return top[(opts.variant ?? 0) % top.length];
}

/**
 * Build the command list that lays the tray out from `fromSpread` onwards.
 * Returns `[]` when there is nothing to place.
 */
export function autoFill(opts: AutoFillOptions): Command[] {
  const { doc, photos } = opts;
  if (opts.target !== undefined) {
    const target = opts.target;
    const elements = target === 'cover' ? doc.cover.elements : (doc.spreads[target]?.elements ?? []);
    if (target === 'cover' || elements.some((el) => el.type === 'photo' && el.slotId !== null)) {
      const used = new Set(elements.flatMap((el) => el.type === 'photo' && el.photoId ? [el.photoId] : []));
      const queue = sortPhotosChronologically(photos).filter((photo) => {
        if (photo.status !== 'ready' || used.has(photo.id)) return false;
        used.add(photo.id);
        return true;
      });
      const commands: Command[] = [];
      for (const el of sortElements(elements)) {
        if (el.type !== 'photo' || el.photoId !== null || el.locked || el.hidden) continue;
        const photo = queue.shift();
        if (!photo) break;
        commands.push({
          type: 'element.update', target, id: el.id,
          patch: { photoId: photo.id, fit: neutralImageFit() },
        });
      }
      return commands;
    }
  }
  const gapMs = opts.gapMs ?? DEFAULT_GAP_MS;
  const maxPerSpread = Math.min(opts.maxPerSpread ?? MAX_LAYOUT_PHOTOS, MAX_LAYOUT_PHOTOS);
  const groups = groupPhotos(photos, gapMs, maxPerSpread);
  if (groups.length === 0) return [];

  const start = opts.fromSpread ?? (typeof opts.target === 'number' ? opts.target : (doc.spreads[0]?.single ? 1 : 0));
  const from = Math.max(0, Math.min(doc.spreads.length, start));
  const family = familyOf(doc.page.widthMm, doc.page.heightMm);

  const commands: Command[] = [];

  // 1. make sure enough spreads exist
  const wantSpreads = from + groups.length;
  let available = doc.spreads.length;
  if (wantSpreads > available && (opts.growAlbum ?? true)) {
    const innerPages = Math.min(
      PAGE_COUNT_LIMITS.max,
      doc.firstPageSingle ? (wantSpreads - 1) * 2 : wantSpreads * 2,
    );
    const grown = spreadCountForPages(innerPages, doc.firstPageSingle);
    if (grown > available) {
      commands.push({ type: 'album.setPageCount', innerPages });
      available = grown;
    }
  }

  // 2. one applyLayout per group, dropping groups that no longer fit
  const singles = new Map<number, 'left' | 'right' | null>();
  doc.spreads.forEach((s, i) => singles.set(i, s.single));
  const lastIndex = available - 1;

  groups.forEach((group, g) => {
    const index = from + g;
    if (index >= available) return;
    // after growing, the last spread is the single closing page
    const isSingle = index === 0 || index === lastIndex ? Boolean(doc.firstPageSingle) : Boolean(singles.get(index));
    const layout = pickLayout({
      photoCount: group.length,
      orientation: dominantOrientation(group),
      family,
      scope: isSingle ? 'page' : 'spread',
      variant: g,
    });
    if (!layout) return;
    // The template's own box: a spread is two pages wide, a single page is one.
    const boxAspect = (doc.page.widthMm * (layout.scope === 'spread' ? 2 : 1)) / doc.page.heightMm;
    const photoSlots = layout.slots.filter((slot) => slot.kind === 'photo');
    commands.push({
      type: 'spread.applyLayout',
      index,
      layout,
      photoIds: orderForSlots(group, photoSlots, boxAspect).map((p) => p.id),
      keepExisting: false,
    });
  });

  return commands;
}
